import { FC, useRef } from "react";
import ReactDatePicker from "react-datepicker";

import "react-datepicker/dist/react-datepicker.css";
import { CalendarIcon } from "../../assets/icons";
import clsx from "clsx";
import { createPortal } from "react-dom";

export type DateRange = {
  from?: Date;
  to?: Date;
};

type Props = {
  onChange: (dateRange: DateRange | undefined) => void;
  value?: DateRange;
  className?: string;
  placeholderText?: string;
  iconClassName?: string;
  dateFormat?: string;
};

const DateRangePicker: FC<Props> = ({
  value,
  onChange,
  className = "",
  placeholderText = "Select date range",
  iconClassName,
  dateFormat = "dd/MM/yyyy",
}) => {
  const ref = useRef<any>(null);

  const handleChange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    if (!start && !end) {
      onChange(undefined);
    } else {
      onChange({
        from: start || undefined,
        to: end || undefined,
      });
    }
  };

  const formatDisplayValue = () => {
    if (!value?.from) return "";
    const fromStr = value.from.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
    if (!value.to) return fromStr;
    const toStr = value.to.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
    return `${fromStr} - ${toStr}`;
  };

  return (
    <div
      className={clsx(
        "cx-w-full cx-rounded-full cx-border cx-border-stroke-primary cx-bg-background-default cx-flex cx-items-center cx-relative",
        className
      )}
    >
      <ReactDatePicker
        ref={ref as any}
        selectsRange
        startDate={value?.from}
        endDate={value?.to}
        onChange={handleChange}
        placeholderText={placeholderText}
        dateFormat={dateFormat}
        className="cx-bg-transparent cx-appearance-none cx-outline-none cx-w-full cx-rounded-full cx-text-sm cx-font-semibold placeholder:cx-text-brand-primary-regular"
        wrapperClassName="cx-w-full"
        popperClassName="cx-z-[100] cx-date-range-picker-dual"
        showIcon={false}
        value={formatDisplayValue()}
        monthsShown={2}
        popperContainer={({ children }) =>
          createPortal(children, document.body)
        }
      />
      <CalendarIcon
        className={clsx(
          "cx-w-5 cx-h-5 cx-cursor-pointer cx-absolute cx-right-3",
          iconClassName
        )}
        onClick={() => {
          ref.current?.setFocus();
        }}
      />
    </div>
  );
};

export default DateRangePicker;
